import * as React from "react";
import { useEffect, useMemo } from "react";
import { FilterExpression } from "Engine/src/Helpers/FilterExpressions/FilterExpression";
import { shallowEqual } from "react-redux";

import { IterUtils } from "../../../../../Common/IterableUtils";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    ModelPath,
    ResolvedModelPath,
} from "../../../../../Common/ModelPath/ModelPath";
import {
    addInstance,
    fillMultiline,
    getChildren,
    LastInstanceBehaviour,
    removeInstance,
} from "../../../Engine/ActionCreators/AllActionCreators";
import { ModelHunk } from "../../../Engine/Core/ModelStoring/ModelHunk";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { withResolvePaths } from "../../withResolvePaths";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import { DisabledPropertyContext } from "../../Services/Contexts/DisabledPropertyContext";
import {
    UserFriendlyLoadingState,
    useUserFriendlyLoadingState,
} from "../../FormParts/UserPicklistDeclaration/SelectNidusEntryModal/SelectEntryModal/UserFriendlyLoadingState";
import { useTypedDispatch, useTypedSelector } from "../../../../../Common/UseTypedSelector";
import { withViewModelSelect } from "../../withViewModelSelect";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { useMultiline } from "../../../Engine/Core/Filtering/UseMultiline";
import { IMultilineManagerClient } from "../../../Engine/Core/Filtering/MultilineManager";
import { createHocDisplayName } from "../../TypingUtils";
import { useCrossfitTableBodyRequestEmptyRowContext } from "../CrossfitTable/Internals/CrossfitTableBody/CrossfitTableBodyRequestEmptyRowContext";
import { isNotEmptyFilters } from "../../../Helpers/FilteringHelpers";

import { MultiLine, MultiLineProps } from "./MultiLine";
import { MultiLineFilters } from "./MultiLineFilter";

export interface MultiLineContainerOwnProps {
    bindingPath: AbsoluteResolvedModelPath;
    viewModelSelect: ViewModelSelect;
    debounceDisable?: boolean;
    columnsStructure?: AbsoluteModelPath[];
    onRowCLick?: FormActionHandler;
    filteredUniqKey?: string;
    defaultFilter?: FilterExpression;
    storeFiltersWithinSession?: boolean;
    instancesPerPage: number;
    children: React.ReactNode;
    dynamicHeight: boolean;
}

export type MultiLineContainerProps = MultiLineContainerOwnProps &
    Omit<Omit<MultiLineProps, keyof ReturnType<typeof useMultilineProps>>, keyof ReturnType<typeof mapDispatchToProps>>;

const mapDispatchToProps = (
    client: IMultilineManagerClient,
    dispatch: CandyDispatch,
    bindingPath: AbsoluteResolvedModelPath,
    filteredUniqKey: string,
    onRowCLick?: FormActionHandler
) => ({
    fillMultiline: (values: ModelHunk, newInstanceCount: number) =>
        dispatch(fillMultiline(bindingPath, values, newInstanceCount, filteredUniqKey)),
    fillInternalMultiField: (pathToMultiField: ResolvedModelPath, values: ModelHunk, newInstanceCount: number) =>
        dispatch(fillMultiline(bindingPath.joinWith(pathToMultiField), values, newInstanceCount, filteredUniqKey)),
    getInternalMultiFiledChildren: (pathToMultiField: ModelPath) =>
        dispatch(getChildren(bindingPath.joinWith(pathToMultiField))),
    addInstance: () => dispatch(addInstance(bindingPath, undefined, undefined, undefined, undefined, filteredUniqKey)),
    goToPage: (page: number) => {
        client.setCurrentPage(page);
    },
    onRemoveInstance: (instanceToRemove: string) => {
        dispatch(removeInstance(bindingPath, instanceToRemove, LastInstanceBehaviour.Remove));
    },
    onRowClick: (instance: string) => {
        dispatch((_, __, form) => {
            if (onRowCLick != undefined && typeof onRowCLick === "function") {
                onRowCLick(form.getHelperFunctionsCallContext(), {
                    bindingPath: bindingPath.joinWith(instance).toLegacyPath(),
                    field: "value",
                });
            }
        });
    },
});

const useMultilineProps = (ownProps: MultiLineContainerOwnProps) => {
    const {
        bindingPath,
        columnsStructure,
        filteredUniqKey = "default",
        defaultFilter,
        debounceDisable,
        onRowCLick,
        viewModelSelect,
    } = ownProps;
    const uniqColumnsStructure = useMemo(
        () => (columnsStructure ? [...IterUtils.distinctBy(columnsStructure, x => x.toLegacyPath())] : undefined),
        [columnsStructure]
    );

    const { disabled, isReadOnly } = useTypedSelector(state => {
        const disabledField = viewModelSelect.pathViewModel(state, bindingPath)?.disabled;
        return {
            disabled: typeof disabledField === "string" ? disabledField === "true" : disabledField,
            isReadOnly: state.settings.isReadOnly,
        };
    }, shallowEqual);

    const { client, currentPage, loading, filters, sort, displayedInstances } = useMultiline({
        bindingPath: bindingPath,
        filteredUniqKey: filteredUniqKey,
        instancesPerPage: ownProps.instancesPerPage,
        storeFiltersWithinSession: ownProps.storeFiltersWithinSession ?? false,
        useShortDebounce: debounceDisable ?? false,
        defaultFilter: defaultFilter,
    });

    const loadingState = loading || false;
    const userFriendlyLoadingState = useUserFriendlyLoadingState(loadingState);
    const isUserFriendlyLoaderEnabled = userFriendlyLoadingState > UserFriendlyLoadingState.LoadingState1;

    return {
        client: client,
        loadingState: loadingState,
        bindingPath: bindingPath,
        filteredUniqKey: filteredUniqKey,
        loading: isUserFriendlyLoaderEnabled,
        displayedInstances: displayedInstances,
        sorted: sort != undefined,
        filters: filters,
        columnsStructure: uniqColumnsStructure,
        currentPage: currentPage,
        instancesPerPage: ownProps.instancesPerPage,
        disabled: disabled,
        isReadOnly: isReadOnly,
        highlightHoveredRow: onRowCLick != undefined,
    };
};

const useEmptyRowRequestUpdate = (filters: MultiLineFilters) => {
    const emptyRowRequester = useCrossfitTableBodyRequestEmptyRowContext();
    const emptyRowRequired = isNotEmptyFilters(filters);
    useEffect(() => {
        emptyRowRequester.requestEmptyRow(emptyRowRequired);
    }, [emptyRowRequired]);
    useEffect(() => () => emptyRowRequester.requestEmptyRow(false), []);
};

function MultiLineContainerWrapper(ownProps: MultiLineContainerProps) {
    const multilineProps = useMultilineProps(ownProps);
    const dispatch = useTypedDispatch();
    const actions = useMemo(
        () =>
            mapDispatchToProps(
                multilineProps.client,
                dispatch,
                ownProps.bindingPath,
                ownProps.filteredUniqKey ?? "default",
                ownProps.onRowCLick
            ),
        [dispatch, ownProps.bindingPath, ownProps.filteredUniqKey, ownProps.onRowCLick, multilineProps.client]
    );

    useEmptyRowRequestUpdate(multilineProps.filters);

    return (
        <DisabledPropertyContext.Provider value={multilineProps.loadingState}>
            <MultiLine {...ownProps} {...multilineProps} {...actions} disableScroll={true}>
                {ownProps.children}
            </MultiLine>
        </DisabledPropertyContext.Provider>
    );
}
MultiLineContainerWrapper.displayName = createHocDisplayName(MultiLine, "MultiLineContainerWrapper");

export const MultiLineContainer = React.memo(
    withResolvePaths(
        withViewModelSelect(
            withFocusManagement(withExternalMountingEventReporting(withControlDebugInfo(MultiLineContainerWrapper)))
        )
    )
);
